import { z } from 'zod';
import {
  ACTIVITY_KINDS,
  guestScopeSchema,
  memberStatusSchema,
  presenceStateSchema,
  roleSchema,
} from '../enums';
import { isoDateTimeSchema, uuidSchema } from '../contracts/common';

/**
 * Realtime-only payload types (PLAN §5 "Shared payload types"). Messages, conversations, member
 * state, reactions, files and badges reuse the REST DTOs from `@orbit/shared/contracts`.
 */

/** A person as the directory shows them. Phone and custom fields come only over REST. */
export const userProfileDtoSchema = z.object({
  userId: uuidSchema,
  displayName: z.string(),
  fullName: z.string(),
  title: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  managerUserId: uuidSchema.nullable(),
  timezone: z.string(),
  /** Clients cache avatars by (userId, avatarVersion) and sign URLs over REST. */
  avatarVersion: z.number().int().nonnegative(),
  role: roleSchema,
  status: memberStatusSchema,
  version: z.number().int().nonnegative(),
});
export type UserProfileDto = z.infer<typeof userProfileDtoSchema>;

/** Aggregated presence of one person (all their devices). `dnd` arrives in Phase 2. */
export const presenceDtoSchema = z.object({
  userId: uuidSchema,
  presence: presenceStateSchema,
  dnd: z.boolean(),
  dndUntil: isoDateTimeSchema.nullable(),
  /** Phase 2 (custom status). */
  status: z
    .object({
      emoji: z.string().nullable(),
      text: z.string().nullable(),
      expiresAt: isoDateTimeSchema.nullable(),
    })
    .nullable(),
  /** Redis TIME in microseconds; apply only when newer. */
  version: z.number().int().nonnegative(),
});
export type PresenceDto = z.infer<typeof presenceDtoSchema>;

/** One Activity item (`activity_items`). The feed endpoints arrive in Phase 2. */
export const activityDtoSchema = z.object({
  id: uuidSchema,
  kind: z.enum(ACTIVITY_KINDS),
  actorId: uuidSchema.nullable(),
  conversationId: uuidSchema.nullable(),
  messageId: uuidSchema.nullable(),
  threadRootId: uuidSchema.nullable(),
  taskId: uuidSchema.nullable(),
  /** ≤ 140 characters of plain text. */
  preview: z.string(),
  createdAt: isoDateTimeSchema,
  readAt: isoDateTimeSchema.nullable(),
});
export type ActivityDto = z.infer<typeof activityDtoSchema>;

/** `notification.created` kinds (web and desktop show an OS notification unless focused). */
export const NOTIFICATION_KINDS = [
  'mention',
  'dm',
  'thread_reply',
  'keyword',
  'reaction',
  'task',
  'decision',
  'reminder',
  'call',
  'ptstdm',
  'meeting',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const memberUpdatedDataSchema = z.object({
  userId: uuidSchema,
  role: roleSchema,
  guestScope: guestScopeSchema.nullable(),
  status: memberStatusSchema,
  departmentId: uuidSchema.nullable(),
  managerUserId: uuidSchema.nullable(),
  title: z.string().nullable(),
  version: z.number().int().nonnegative(),
});
